import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Wind, Waves, Thermometer, Cloud } from "lucide-react";
import { Link } from "react-router-dom";

const compass16 = (deg) => {
  if (deg == null) return "";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
};

export default function WeatherWidget() {
  const t = useT();
  const [live, setLive] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/weather/live");
        if (!cancel) setLive(data);
      } catch { if (!cancel) setErr(true); }
    })();
    return () => { cancel = true; };
  }, []);

  if (err) return null;
  if (!live) return (
    <div className="ledger-card p-4 font-mono text-[10px] text-[#94A3B8]" data-testid="dashboard-weather-loading">{t("weather.loading")}</div>
  );

  const wind = live.wind_kt;
  const tone = wind == null ? "text-[#94A3B8]" : wind >= 22 ? "text-[#F87171]" : wind >= 15 ? "text-[#F59E0B]" : "text-[#34D399]";

  return (
    <Link to="/app/weather" className="ledger-card p-4 block hover:border-[#4DA6FF] transition-colors" data-testid="dashboard-weather-widget">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-widest text-[#4DA6FF]">{t("weather.section")}</div>
        <div className="font-mono text-[10px] text-[#64748B]">{live.location?.name}</div>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-3">
        <div className="flex items-center gap-2">
          <Thermometer className="w-4 h-4 text-[#4DA6FF]" />
          <div>
            <div className="font-mono text-[9px] text-[#94A3B8]">{t("weather.temp")}</div>
            <div className="font-display text-sm font-bold">{live.temp_c != null ? `${live.temp_c.toFixed(0)}°` : "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Wind className={`w-4 h-4 ${tone}`} />
          <div>
            <div className="font-mono text-[9px] text-[#94A3B8]">{t("weather.wind")}</div>
            <div className={`font-display text-sm font-bold ${tone}`}>{wind != null ? `${wind} kt` : "—"}</div>
            <div className="font-mono text-[9px] text-[#64748B]">{compass16(live.wind_dir_deg)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Waves className="w-4 h-4 text-[#4DA6FF]" />
          <div>
            <div className="font-mono text-[9px] text-[#94A3B8]">{t("weather.wave")}</div>
            <div className="font-display text-sm font-bold">{live.wave_m != null ? `${live.wave_m} m` : "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Cloud className="w-4 h-4 text-[#4DA6FF]" />
          <div>
            <div className="font-mono text-[9px] text-[#94A3B8]">{t("weather.cloud")}</div>
            <div className="font-display text-sm font-bold">{live.cloud_pct != null ? `${live.cloud_pct}%` : "—"}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
